/**
 * Shared entity matching utilities.
 *
 * Used by both corroboration engine and entity resolver to avoid
 * duplicating identifier validation and name normalization logic.
 */

import type { Entity, EntityIdentifier } from './types';

// ---------------------------------------------------------------------------
// Identifier extraction + validation
// ---------------------------------------------------------------------------

/** Extract typed identifiers from an entity's properties. */
export function getIdentifiers(entity: Entity): EntityIdentifier[] {
  const identifiers = entity.properties?.identifiers;
  if (!Array.isArray(identifiers)) return [];
  return identifiers.filter(
    (id): id is EntityIdentifier =>
      typeof id === 'object' && id !== null &&
      typeof id.type === 'string' && typeof id.value === 'string',
  );
}

/**
 * Validate identifier format.
 *   MMSI: 9 digits
 *   IMO: 7 digits
 *   ICAO24: 6 hex characters
 *   Unknown types: non-empty value
 */
export function isValidIdentifier(id: EntityIdentifier): boolean {
  switch (id.type.toUpperCase()) {
    case 'MMSI':
      return /^\d{9}$/.test(id.value);
    case 'IMO':
      return /^\d{7}$/.test(id.value);
    case 'ICAO24':
      return /^[0-9a-fA-F]{6}$/.test(id.value);
    default:
      return id.value.length > 0;
  }
}

// ---------------------------------------------------------------------------
// Name normalization
// ---------------------------------------------------------------------------

/** Normalize a name for comparison: lowercase, trim, collapse whitespace. */
export function normalizeName(name: string): string {
  return name.toLowerCase().trim().replace(/\s+/g, ' ');
}
